import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';

const PackageDealForm = ({ onSave, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    packageType: initialData?.packageType || 'hotel-flight',
    // Hotel fields
    propertyName: initialData?.propertyName || '',
    hotelLocation: initialData?.hotelLocation || '',
    checkInDate: initialData?.checkInDate || '',
    checkInTime: initialData?.checkInTime || '',
    checkOutDate: initialData?.checkOutDate || '',
    checkOutTime: initialData?.checkOutTime || '',
    roomType: initialData?.roomType || '',
    // Flight fields (for hotel+flight)
    airline: initialData?.airline || '',
    flightNumber: initialData?.flightNumber || '',
    departureAirport: initialData?.departureAirport || '',
    arrivalAirport: initialData?.arrivalAirport || '',
    // Car fields (for hotel+car)
    rentalAgency: initialData?.rentalAgency || '',
    carClass: initialData?.carClass || '',
    pickupLocation: initialData?.pickupLocation || '',
    dropoffLocation: initialData?.dropoffLocation || '',
    // Activity fields (for hotel+activity)
    activityName: initialData?.activityName || '',
    activityDate: initialData?.activityDate || '',
    activityTime: initialData?.activityTime || '',
    activityLocation: initialData?.activityLocation || '',
    // Common fields
    confirmationNumber: initialData?.confirmationNumber || '',
    description: initialData?.description || '',
    price: initialData?.price || '',
    depositRequired: initialData?.depositRequired || false,
    depositAmount: initialData?.depositAmount || '',
    depositDueDate: initialData?.depositDueDate || '',
    isOption: initialData?.isOption || false,
    optionDescription: initialData?.optionDescription || '',
    paymentTiming: initialData?.paymentTiming || 'with_deposit',
    optionalDepositAmount: initialData?.optionalDepositAmount || '',
    photos: initialData?.photos || [],
    documents: initialData?.documents || []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      type: 'package',
      price: parseFloat(formData.price) || 0,
      paymentTiming: formData.paymentTiming,
      optionalDepositAmount: formData.optionalDepositAmount ? parseFloat(formData.optionalDepositAmount) : null,
      depositAmount: formData.depositRequired ? parseFloat(formData.depositAmount) || 0 : 0
    });
  };

  return (
    <form onSubmit={handleSubmit} className="component-form package-deal-form">
      <div className="form-section">
        <h4>Package Information</h4>
        
        <div className="form-group">
          <label>Title/Description*</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="e.g., NYC Weekend Getaway Package"
            required
          />
        </div>

        <div className="form-group">
          <label>Package Type*</label>
          <select
            value={formData.packageType}
            onChange={(e) => setFormData({...formData, packageType: e.target.value})}
            required
          >
            <optgroup label="2-Component Packages">
              <option value="hotel-flight">Hotel + Flight</option>
              <option value="hotel-car">Hotel + Car Rental</option>
              <option value="hotel-activity">Hotel + Activity/Tour</option>
            </optgroup>
            <optgroup label="3-Component Packages">
              <option value="hotel-flight-car">Hotel + Flight + Car Rental</option>
              <option value="hotel-flight-activity">Hotel + Flight + Activity</option>
              <option value="hotel-car-activity">Hotel + Car + Activity</option>
            </optgroup>
            <optgroup label="Complete Package">
              <option value="hotel-flight-car-activity">Hotel + Flight + Car + Activity</option>
            </optgroup>
          </select>
        </div>
      </div>

      <div className="form-section">
        <h4>Hotel Information</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Property Name*</label>
            <input
              type="text"
              value={formData.propertyName}
              onChange={(e) => setFormData({...formData, propertyName: e.target.value})}
              placeholder="e.g., Grand Hyatt New York"
              required
            />
          </div>
          <div className="form-group">
            <label>Location*</label>
            <input
              type="text"
              value={formData.hotelLocation}
              onChange={(e) => setFormData({...formData, hotelLocation: e.target.value})}
              placeholder="e.g., New York, NY"
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Check-in Date*</label>
            <input
              type="date"
              value={formData.checkInDate}
              onChange={(e) => setFormData({...formData, checkInDate: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Check-in Time</label>
            <input
              type="time"
              value={formData.checkInTime}
              onChange={(e) => setFormData({...formData, checkInTime: e.target.value})}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Check-out Date*</label>
            <input
              type="date"
              value={formData.checkOutDate}
              onChange={(e) => setFormData({...formData, checkOutDate: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Check-out Time</label>
            <input
              type="time"
              value={formData.checkOutTime}
              onChange={(e) => setFormData({...formData, checkOutTime: e.target.value})}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Room Type</label>
          <input
            type="text"
            value={formData.roomType}
            onChange={(e) => setFormData({...formData, roomType: e.target.value})}
            placeholder="e.g., Deluxe King Room"
          />
        </div>
      </div>

      {/* Flight Section (for packages including flight) */}
      {formData.packageType.includes('flight') && (
        <div className="form-section">
          <h4>Flight Information</h4>
          
          <div className="form-row">
            <div className="form-group">
              <label>Airline*</label>
              <input
                type="text"
                value={formData.airline}
                onChange={(e) => setFormData({...formData, airline: e.target.value})}
                placeholder="e.g., Delta Airlines"
                required
              />
            </div>
            <div className="form-group">
              <label>Flight Number(s)</label>
              <input
                type="text"
                value={formData.flightNumber}
                onChange={(e) => setFormData({...formData, flightNumber: e.target.value})}
                placeholder="e.g., DL1234, DL5678"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Departure Airport*</label>
              <input
                type="text"
                value={formData.departureAirport}
                onChange={(e) => setFormData({...formData, departureAirport: e.target.value})}
                placeholder="e.g., LAX"
                required
              />
            </div>
            <div className="form-group">
              <label>Arrival Airport*</label>
              <input
                type="text"
                value={formData.arrivalAirport}
                onChange={(e) => setFormData({...formData, arrivalAirport: e.target.value})}
                placeholder="e.g., JFK"
                required
              />
            </div>
          </div>
        </div>
      )}

      {/* Car Rental Section (for packages including car) */}
      {formData.packageType.includes('car') && (
        <div className="form-section">
          <h4>Car Rental Information</h4>
          
          <div className="form-row">
            <div className="form-group">
              <label>Rental Agency*</label>
              <input
                type="text"
                value={formData.rentalAgency}
                onChange={(e) => setFormData({...formData, rentalAgency: e.target.value})}
                placeholder="e.g., Enterprise"
                required
              />
            </div>
            <div className="form-group">
              <label>Car Class*</label>
              <input
                type="text"
                value={formData.carClass}
                onChange={(e) => setFormData({...formData, carClass: e.target.value})}
                placeholder="e.g., Economy, SUV"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Pickup Location*</label>
              <input
                type="text"
                value={formData.pickupLocation}
                onChange={(e) => setFormData({...formData, pickupLocation: e.target.value})}
                placeholder="e.g., Airport"
                required
              />
            </div>
            <div className="form-group">
              <label>Dropoff Location*</label>
              <input
                type="text"
                value={formData.dropoffLocation}
                onChange={(e) => setFormData({...formData, dropoffLocation: e.target.value})}
                placeholder="e.g., Airport"
                required
              />
            </div>
          </div>
        </div>
      )}

      {/* Activity Section (for packages including activity) */}
      {formData.packageType.includes('activity') && (
        <div className="form-section">
          <h4>Activity/Tour Information</h4>
          
          <div className="form-group">
            <label>Activity Name*</label>
            <input
              type="text"
              value={formData.activityName}
              onChange={(e) => setFormData({...formData, activityName: e.target.value})}
              placeholder="e.g., City Sightseeing Tour"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Activity Date*</label>
              <input
                type="date"
                value={formData.activityDate}
                onChange={(e) => setFormData({...formData, activityDate: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Activity Time</label>
              <input
                type="time"
                value={formData.activityTime}
                onChange={(e) => setFormData({...formData, activityTime: e.target.value})}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Activity Location</label>
            <input
              type="text"
              value={formData.activityLocation}
              onChange={(e) => setFormData({...formData, activityLocation: e.target.value})}
              placeholder="e.g., Times Square Meeting Point"
            />
          </div>
        </div>
      )}

      <div className="form-section">
        <h4>Booking Details</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Confirmation Number</label>
            <input
              type="text"
              value={formData.confirmationNumber}
              onChange={(e) => setFormData({...formData, confirmationNumber: e.target.value})}
              placeholder="e.g., PKG123456"
            />
          </div>
          <div className="form-group">
            <label>Total Package Price*</label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>Package Inclusions & Notes</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="List what's included in the package, terms, restrictions, etc..."
            rows="4"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Deposit Information</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.depositRequired}
                onChange={(e) => setFormData({...formData, depositRequired: e.target.checked})}
              />
              Deposit Required
            </label>
          </div>
        </div>

        {formData.depositRequired && (
          <div className="form-row">
            <div className="form-group">
              <label>Deposit Amount*</label>
              <input
                type="number"
                value={formData.depositAmount}
                onChange={(e) => setFormData({...formData, depositAmount: e.target.value})}
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="form-group">
              <label>Deposit Due Date</label>
              <input
                type="date"
                value={formData.depositDueDate}
                onChange={(e) => setFormData({...formData, depositDueDate: e.target.value})}
              />
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h4>Client Options</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isOption}
                onChange={(e) => setFormData({...formData, isOption: e.target.checked})}
              />
              Mark as Optional for Client
            </label>
            <small className="form-help">
              When checked, clients can choose whether to include this component in their booking
            </small>
          </div>
        </div>

        {formData.isOption && (
          <>
            <div className="form-group">
              <label>Option Description for Client</label>
              <textarea
                value={formData.optionDescription}
                onChange={(e) => setFormData({...formData, optionDescription: e.target.value})}
                placeholder="e.g., Optional package upgrade or alternative"
                rows="2"
              />
              <small className="form-help">
                This description helps clients understand what this option includes
              </small>
            </div>

            <div className="form-group">
              <label>Payment Timing for Optional Component</label>
              <select
                value={formData.paymentTiming}
                onChange={(e) => setFormData({...formData, paymentTiming: e.target.value})}
                className="form-control"
              >
                <option value="immediate">Pay Full Price When Selected (e.g., cruise excursions)</option>
                <option value="with_deposit">Pay Deposit When Selected</option>
                <option value="with_final">Pay with Final Payment (nothing due initially)</option>
                <option value="bundled">Already Included in Package (no additional payment)</option>
              </select>
              <small className="form-help">
                Choose when payment is due if client selects this option
              </small>
            </div>

            {formData.paymentTiming === 'with_deposit' && (
              <div className="form-group">
                <label>Optional Component Deposit Amount ($)</label>
                <input
                  type="number"
                  value={formData.optionalDepositAmount}
                  onChange={(e) => setFormData({...formData, optionalDepositAmount: e.target.value})}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="form-control"
                />
                <small className="form-help">
                  Amount due when client selects this option (leave blank for full price)
                </small>
              </div>
            )}
          </>
        )}
      </div>

      <AttachmentUpload
        photos={formData.photos}
        documents={formData.documents}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Add'} Package
        </button>
      </div>
    </form>
  );
};

export default PackageDealForm;
